import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import { Checkbox, Divider, Flex } from 'antd';
import Modifier from './Modifier';
import { modifierContext } from '../ModifierProvider';
import { getActiveModifierOptions, getModifierMaxSelections, isAutoApplied } from '../utils';
import OptionRow from '../OptionRow';
import './style.css';
import QuantityInput from 'components/QuantityInput';

function MultiSelectModifier({ modifier, disabled }) {
  const activeOptions = useMemo(() => getActiveModifierOptions(modifier), [modifier]);

  return (
    <Flex vertical>
      {activeOptions.map((option, index) => (
        <ModifierOption modifier={modifier} option={option} disabled={disabled} showDivider={index !== activeOptions.length - 1} key={option.id} />
      ))}
    </Flex>
  );
}

function ModifierOption({
  modifier, disabled, option, showDivider,
}) {
  const maxSelections = getModifierMaxSelections(modifier);
  const {
    isSelected, handleChange, getTotalQty, getSelectedOption, getNestedOptions, hasSelectedNestedOptions,
  } =
    useContext(modifierContext);
  // Auto-expand if this option has selected nested options (e.g., when editing)
  const [showChildren, setShowChildren] = useState(false);
  const { supports_multiple_quantity: supportMultipleQty } = option;
  const selected = isSelected({ option, modifier });
  const nestedModifiers = useMemo(() => getNestedOptions(option), [getNestedOptions, option]);

  // Auto-expand nested modifiers when editing if they have selected options
  useEffect(() => {
    if (hasSelectedNestedOptions(option)) {
      setShowChildren(true);
    }
  }, [option, hasSelectedNestedOptions]);
  const qty = getSelectedOption({ option, modifier })?.qty ?? 0;
  const otherQty = getTotalQty(modifier) - qty;
  const maxQty = Math.min(maxSelections - otherQty, option.max_qty || 100);

  if (isAutoApplied(modifier) && !selected) {
    handleChange({ modifier, option, value: { ...option, qty: 1 } });
  }
  if (supportMultipleQty) {
    return (
      <Flex gap={8} key={option.id} align="center">
        <QuantityInput
          value={qty}
          min={0}
          max={maxQty}
          style={{ flexShrink: 0 }}
          className="py-2"
          onChange={(qty) => {
            if (qty === 0) {
              return handleChange({ option, modifier, value: undefined });
            }
            handleChange({ option, modifier, value: { ...option, qty } });
          }}
          disabled={disabled}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <OptionRow option={option} />
        </div>
      </Flex>
    );
  }
  return (
    <div key={option.id}>
      <Checkbox
        className="omc-select-row"
        checked={isSelected({ option, modifier })}
        onChange={() => {
          setShowChildren(!showChildren);
          if (selected) {
            return handleChange({ option, modifier, value: undefined });
          }
          handleChange({
            option,
            modifier,
            value: { ...option, qty: 1, modifierTitle: modifier?.title },
          });
        }}
        disabled={
          disabled || (maxQty === 0 && !isSelected({ modifier, option }))
        }
      >
        <OptionRow option={option} showChildren={showChildren} />
      </Checkbox>
      {showChildren && nestedModifiers?.length > 0 && nestedModifiers.map((m, index) => (
        <Modifier modifier={m} key={m.id ?? index} isNested />
      ))}
      {showDivider && <Divider className="my-3" />}
    </div>
  );
}

export default MultiSelectModifier;
